import React from 'react';
import { Search, X } from 'lucide-react';
import type { ContractStatus } from '../../lib/contract-status';

export interface ContractsFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  stateFilter: string;
  onStateFilterChange: (state: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  availableStates?: string[];
  availableStatuses?: ContractStatus[];
  onClearFilters?: () => void;
}

const stateLabels: Record<string, string> = {
  PR: 'Puerto Rico',
  FL: 'Florida',
  TX: 'Texas',
  CA: 'California',
  NY: 'New York',
  LA: 'Louisiana',
};

const statusLabels: Record<ContractStatus, string> = {
  uploaded: 'Uploaded',
  processing: 'Processing',
  ocr_complete: 'OCR Complete',
  transferring_to_r2: 'Transferring to R2',
  processed: 'Processed',
  extracting: 'Extracting',
  extraction_complete: 'Extraction Done',
  extraction_failed: 'Extraction Failed',
  structured: 'Structured',
  embedded: 'Embedded',
  linked: 'Linked',
  ready: 'Ready',
  error: 'Error',
  processing_failed: 'Failed',
};

const defaultStates = ['PR', 'FL', 'TX', 'CA', 'NY'];
const defaultStatuses: ContractStatus[] = [
  'uploaded',
  'processing',
  'processed',
  'structured',
  'embedded',
  'linked',
  'ready',
  'error',
];

export const ContractsFilters: React.FC<ContractsFiltersProps> = ({
  searchQuery,
  onSearchChange,
  stateFilter,
  onStateFilterChange,
  statusFilter,
  onStatusFilterChange,
  availableStates = defaultStates,
  availableStatuses = defaultStatuses,
  onClearFilters,
}) => {
  const hasActiveFilters = searchQuery || stateFilter || statusFilter;

  return (
    <div className="bg-white rounded-xl shadow-card border border-neutral-100 p-4" data-testid="contracts-filters">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <div className="input-icon-left">
            <Search className="h-5 w-5" />
          </div>
          <input
            type="text"
            placeholder="Search contracts by name..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="input input-with-icon-left"
            data-testid="search-input"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Clear search"
              data-testid="clear-search"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="flex gap-3">
          {/* State/Territory Filter */}
          <div className="relative">
            <select
              value={stateFilter}
              onChange={(e) => onStateFilterChange(e.target.value)}
              className="select min-w-[140px]"
              data-testid="state-filter"
            >
              <option value="">All States/Territories</option>
              {availableStates.map((state) => (
                <option key={state} value={state}>
                  {stateLabels[state] || state}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              className="select min-w-[140px]"
              data-testid="status-filter"
            >
              <option value="">All Statuses</option>
              {availableStatuses.map((status) => (
                <option key={status} value={status}>
                  {statusLabels[status]}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && onClearFilters && (
            <button
              onClick={onClearFilters}
              className="btn-outline btn-sm"
              data-testid="clear-filters"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="active-filters">
          {searchQuery && (
            <span className="badge-primary">
              Search: "{searchQuery}"
              <button
                onClick={() => onSearchChange('')}
                className="ml-2 hover:text-primary-600 transition-colors"
                aria-label="Remove search filter"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {stateFilter && (
            <span className="badge-neutral">
              State/Territory: {stateLabels[stateFilter] || stateFilter}
              <button
                onClick={() => onStateFilterChange('')}
                className="ml-2 hover:text-neutral-600 transition-colors"
                aria-label="Remove state/territory filter"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {statusFilter && (
            <span className="badge-success">
              Status: {statusLabels[statusFilter as ContractStatus]}
              <button
                onClick={() => onStatusFilterChange('')}
                className="ml-2 hover:text-success-600 transition-colors"
                aria-label="Remove status filter"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};
